import { inject } from '@angular/core';
import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { EMPTY, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { AuthenticationService } from '../rest/authentication.service';
import { ErrorResponseDialogComponent } from '@shared/error-response-dialog/error-response-dialog.component';
import { MatDialog } from '@angular/material/dialog';

export const SKIP_ERROR_INTERCEPTOR = new HttpContextToken<boolean>(() => false);

/** JSON.stringify that never throws (handles circular refs and BigInt) and caps the output length. */
const safeStringify = (value: unknown, maxLength = 4000): string => {
  try {
    const seen = new WeakSet();
    const json = JSON.stringify(value, (_key, val) => {
      if (typeof val === 'bigint') return val.toString();
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return '[Circular]';
        seen.add(val);
      }

      return val;
    });

    return json.length > maxLength ? `${json.slice(0, maxLength)}…[truncated]` : json;
  } catch {
    return String(value);
  }
};

export const httpErrorInterceptorFn: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthenticationService);
  const dialog = inject(MatDialog);

  if (req.context.get(SKIP_ERROR_INTERCEPTOR)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Observability must never break error handling — keep the whole capture in a guard.
      try {
        const httpError = new Error(`${error.status} ${req.method} ${error.url}`);
        httpError.name = 'HttpError';

        Sentry.captureException(httpError, {
          tags: { kind: 'http', source: 'http-error.interceptor', status: String(error.status), method: req.method },
          extra: {
            url: error.url,
            status: error.status,
            method: req.method,
            body: safeStringify(error),
            requestBody: safeStringify(req.body),
          },
          fingerprint: ['http', req.method, String(error.status), error.url ?? 'unknown'],
        });
      } catch (loggingError) {
        // Logging must not break the error flow, but at least surface that it failed.
        // (captureConsoleIntegration forwards console.error to Sentry on prod/staging.)
        console.error('Failed to report HTTP error to Sentry', loggingError, error);
      }

      if (error.status === 401) {
        authService.logOut();

        return EMPTY;
      }
      if (error.status === 500) {
        dialog.open(ErrorResponseDialogComponent, { width: '600px' });
      }

      return throwError(() => error);
    }),
  );
};
